import React, { useState, useCallback, useMemo, useEffect } from "react";
import Logger from "../../../../services/Logger";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LuMoveHorizontal, LuMoveVertical } from "react-icons/lu";
import ApiService from "../../../../services/ApiService";
import CrossCandEntry from "./CrossCandEntry";
import { generatePositions, getGridWord } from "./CrossCandUtils";
import { DIRECTION_HORIZONTAL, DIRECTION_VERTICAL } from "../../../pzl_common/PuzzleUtil";

// Normalize French word by removing accents
const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase();
};

// Helper function to get direction-specific label
const getDirectionLabel = (direction, leftToRight) => {
    switch (direction) {
        case DIRECTION_HORIZONTAL:
            return leftToRight ? "Left to Right" : "Right to Left";
        case DIRECTION_VERTICAL:
            return leftToRight ? "Top to Bottom" : "Bottom to Top";
        default:
            return leftToRight ? "Forward" : "Reverse";
    }
};

export default function CrossCandAI({
    onWordAdd,
    onWordSelect,
    selectedManualWord,
    onShowSnackbar,
    square,
    crossTpl
}) {
    const [aiWord, setAiWord] = useState('');
    const [aiCandidates, setAiCandidates] = useState([]);
    const [aiLoading, setAiLoading] = useState(false);
    const [manualRow, setManualRow] = useState(0);
    const [manualCol, setManualCol] = useState(0);
    const [manualDirection, setManualDirection] = useState(DIRECTION_HORIZONTAL);
    const [leftToRight, setLeftToRight] = useState(true);
    const [filterByLength, setFilterByLength] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Calculate actual filled cells by considering unique coordinates
    const calculateFilledCells = useCallback(() => {
        if (!crossTpl?.filledCoordinates) return 0;
        try {
            const filledCoordinates = JSON.parse(crossTpl.filledCoordinates);
            const uniqueCells = new Set();

            filledCoordinates.forEach(word => {
                if (word.text && word.positions) {
                    // Add each position coordinate to the set
                    word.positions.forEach(pos => {
                        uniqueCells.add(`${pos.row}-${pos.col}`);
                    });
                }
            });

            return uniqueCells.size;
        } catch (error) {
            Logger.error("Error calculating filled cells:", error);
            return 0;
        }
    }, [crossTpl?.filledCoordinates]);

    const emptyCellsCount = 144 - calculateFilledCells();

    // Handle AI word search
    const handleAISearch = useCallback(async () => {
        if (!aiWord.trim()) {
            onShowSnackbar("Please enter a word", "error");
            return;
        }

        setAiLoading(true);
        setAiCandidates([]);
        setFilterByLength(false); // Reset filter when fetching new candidates

        try {
            const language = crossTpl?.language || 'english';
            const params = {
                title: aiWord.trim(),
                language: language
            };
            const result = await ApiService.getData("/cr/bedrock/word-associations", params);
            
            if (result && result.associations && Array.isArray(result.associations)) {
                // Transform associations into candidate format
                const transformedCandidates = [];
                result.associations.forEach((word, index) => {
                    // Normalize word
                    let normalizedWord = word;
                    if (language === 'french') {
                        normalizedWord = normalizeFrenchWord(word);
                    } else {
                        normalizedWord = word.toLowerCase();
                    }

                    // Remove hyphens and spaces for grid placement (e.g. "feu de camp" -> "feudecamp")
                    const gridWord = getGridWord(normalizedWord);

                    // Add normal word (left-to-right/top-to-bottom)
                    if (leftToRight) {
                        transformedCandidates.push({
                            word: word, // Display version (original)
                            gridWord: gridWord, // Grid version without hyphens
                            text: normalizedWord,
                            score: result.associations.length - index,
                            startRow: manualRow,
                            startCol: manualCol,
                            direction: manualDirection,
                            positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, false),
                            isReversed: false
                        });
                    }
                    // Add reversed word (right-to-left/bottom-to-top) - only if leftToRight is disabled
                    if (!leftToRight) {
                        transformedCandidates.push({
                            word: word, // Display version (original)
                            gridWord: gridWord, // Grid version without hyphens
                            text: normalizedWord,
                            score: result.associations.length - index,
                            startRow: manualRow,
                            startCol: manualCol,
                            direction: manualDirection,
                            positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, true),
                            isReversed: true
                        });
                    }
                });

                setAiCandidates(transformedCandidates);

                if (transformedCandidates.length === 0) {
                    onShowSnackbar("No associations found", "info");
                } else {
                    onShowSnackbar(`Found ${transformedCandidates.length} associations`, "success");
                }
            } else {
                onShowSnackbar("No associations found for this word", "info");
            }
        } catch (error) {
            Logger.error("Error fetching word associations:", error);
            onShowSnackbar("Error fetching word associations. Please try again.", "error");
        } finally {
            setAiLoading(false);
        }
    }, [aiWord, crossTpl?.language, manualRow, manualCol, manualDirection, leftToRight, onShowSnackbar]);

    // Update positions when row/col/direction changes
    const memoizedCandidates = useMemo(() => {
        if (!aiCandidates || aiCandidates.length === 0) {
            return [];
        }
        
        let filteredCandidates = aiCandidates;
        
        // Filter by length if filterByLength is enabled
        if (filterByLength) {
            filteredCandidates = aiCandidates.filter(candidate => {
                const gridWord = getGridWord(candidate.gridWord || candidate.word);
                return gridWord.length === emptyCellsCount;
            });
        }
        
        return filteredCandidates
            .map(candidate => {
                // Recalculate positions based on current manualRow, manualCol, and direction
                const gridWord = getGridWord(candidate.gridWord || candidate.word);
                const updatedPositions = generatePositions(
                    manualRow,
                    manualCol,
                    manualDirection,
                    gridWord.length,
                    gridWord,
                    candidate.isReversed
                );
                
                return {
                    ...candidate,
                    startRow: manualRow,
                    startCol: manualCol,
                    direction: manualDirection,
                    positions: updatedPositions,
                    key: `${candidate.word}-${manualRow}-${manualCol}-${manualDirection}-${candidate.isReversed}`
                };
            })
            .sort((a, b) => (b.score || 0) - (a.score || 0)); // Sort by score descending
    }, [aiCandidates, manualRow, manualCol, manualDirection, filterByLength, emptyCellsCount, refreshTrigger]);

    // Update form fields when a word is selected externally
    useEffect(() => {
        if (selectedManualWord) {
            setManualRow(selectedManualWord.startRow || 0);
            setManualCol(selectedManualWord.startCol || 0);
            setManualDirection(selectedManualWord.direction || DIRECTION_HORIZONTAL);
            setLeftToRight(!selectedManualWord.isReversed);
        }
    }, [selectedManualWord]);

    const handleDirectionChange = (newDirection) => {
        setManualDirection(newDirection);
        if (selectedManualWord) {
            const gridWord = getGridWord(selectedManualWord.gridWord || selectedManualWord.word);
            const updatedWord = {
                ...selectedManualWord,
                direction: newDirection,
                positions: generatePositions(manualRow, manualCol, newDirection, gridWord.length, gridWord, selectedManualWord.isReversed)
            };
            onWordSelect(updatedWord);
        }
    };

    const handleLeftToRightChange = (newLeftToRight) => {
        setLeftToRight(newLeftToRight);
        if (selectedManualWord) {
            const isReversed = !newLeftToRight;
            const gridWord = getGridWord(selectedManualWord.gridWord || selectedManualWord.word);
            const updatedWord = {
                ...selectedManualWord,
                isReversed: isReversed,
                positions: generatePositions(manualRow, manualCol, manualDirection, gridWord.length, gridWord, isReversed)
            };
            onWordSelect(updatedWord);
        }
    };

    const handleManualWordAdd = async (candidate) => {
        const added = await onWordAdd(candidate);
        if (added) {
            setAiWord('');
            setAiCandidates([]);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, height: '100%', flex: 1 }}>
            {/* AI Word Input Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Word"
                        value={aiWord}
                        onChange={(e) => setAiWord(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && aiWord.trim() && !aiLoading) {
                                handleAISearch();
                            }
                        }}
                        placeholder="Enter a word or sentence"
                        helperText="Enter a word or sentence to get AI-generated associations"
                        size="small"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <span>
                                            <IconButton
                                                onClick={handleAISearch}
                                                disabled={!aiWord.trim() || aiLoading}
                                                color="primary"
                                                size="small"
                                            >
                                                {aiLoading ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <AutoAwesomeIcon />
                                                )}
                                            </IconButton>
                                        </span>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                        label="Row"
                        type="number"
                        value={manualRow}
                        onChange={(e) => setManualRow(Math.max(0, Math.min(11, parseInt(e.target.value) || 0)))}
                        inputProps={{ min: 0, max: 11 }}
                        size="small"
                        sx={{ width: 80 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <TextField
                            label="Column"
                            type="number"
                            value={manualCol}
                            onChange={(e) => setManualCol(Math.max(0, Math.min(11, parseInt(e.target.value) || 0)))}
                            inputProps={{ min: 0, max: 11 }}
                            size="small"
                            sx={{ width: 80 }}
                        />
                        <IconButton
                            onClick={() => {
                                // Force update by incrementing refresh trigger
                                // This will cause memoizedCandidates to recalculate
                                setRefreshTrigger(prev => prev + 1);
                            }}
                            size="small"
                            color="primary"
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="caption"
                            color="error"
                            onClick={() => setFilterByLength(!filterByLength)}
                            sx={{
                                fontWeight: 'bold',
                                letterSpacing: 0.5,
                                fontSize: '0.75rem',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: (theme) => filterByLength 
                                    ? theme.palette.error.main 
                                    : theme.palette.error[theme.palette.mode === 'dark' ? 800 : 50],
                                color: filterByLength ? 'white' : 'error.main',
                                border: (theme) => `1px solid ${theme.palette.error.main}`,
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.error.main,
                                    color: 'white'
                                }
                            }}
                        >
                            Empty: {emptyCellsCount}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Direction:
                    </Typography>
                    <RadioGroup
                        row
                        value={manualDirection}
                        onChange={(e) => handleDirectionChange(e.target.value)}
                    >
                        <FormControlLabel
                            value={DIRECTION_HORIZONTAL}
                            control={<Radio size="small" />}
                            label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveHorizontal /></Box>}
                        />
                        <FormControlLabel
                            value={DIRECTION_VERTICAL}
                            control={<Radio size="small" />}
                            label={<Box sx={{ display: 'flex', alignItems: 'center' }}><LuMoveVertical /></Box>}
                        />
                    </RadioGroup>
                    <ToggleButtonGroup
                        value={leftToRight ? "leftToRight" : "rightToLeft"}
                        exclusive
                        onChange={(e, newValue) => {
                            if (newValue !== null) {
                                handleLeftToRightChange(newValue === "leftToRight");
                            }
                        }}
                        size="small"
                        sx={{ mt: 1 }}
                    >
                        <ToggleButton value="leftToRight">
                            {manualDirection === DIRECTION_HORIZONTAL ? "Left to Right" :
                             manualDirection === DIRECTION_VERTICAL ? "Top to Bottom" :
                             "Forward"}
                        </ToggleButton>
                        <ToggleButton value="rightToLeft">
                            {manualDirection === DIRECTION_HORIZONTAL ? "Right to Left" :
                             manualDirection === DIRECTION_VERTICAL ? "Bottom to Top" :
                             "Reverse"}
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {getDirectionLabel(manualDirection, leftToRight)}
                    </Typography>
                </Box>
            </Box>

            {/* AI Candidates Section */}
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Word Candidates
                </Typography>
                {aiLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Fetching associations...</Typography>
                    </Box>
                ) : (
                    <List sx={{ flex: 1, overflow: 'auto' }}>
                        {memoizedCandidates.length === 0 ? (
                            <ListItem>
                                <ListItemText
                                    primary="No candidates found"
                                    secondary="Enter a word and click the icon to get AI associations"
                                />
                            </ListItem>
                        ) : (
                            memoizedCandidates.map((candidate) => (
                                <CrossCandEntry
                                    key={candidate.key}
                                    candidate={candidate}
                                    isSelected={selectedManualWord?.word === candidate.word}
                                    onWordSelect={onWordSelect}
                                    onWordAdd={handleManualWordAdd}
                                    mode="manual"
                                />
                            ))
                        )}
                    </List>
                )}
            </Box>
        </Box>
    );
}

